import {
	CheckCircle2,
	CircleAlert,
	HardHat,
	MailPlus,
	User,
	Users,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAttendanceDays } from "@/features/presence/hooks/use-attendance-days";
import { useTeamTimeEntries } from "@/features/presence/hooks/use-team-time-entries";
import type { WeekRange } from "@/features/presence/types";
import { countPendingDays } from "@/features/presence/utils/attendance";
import { countPresentWorkers } from "@/features/presence/utils/day";
import { dayRange, weekRange } from "@/features/presence/utils/week";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { currentWorkDay } from "@/lib/format";
import { useInvites } from "../../hooks/use-invites";
import { activeWorkers, useWorkers } from "../../hooks/use-workers";
import { KpiCard } from "../kpi-card";

function crewSummary(
	engineers: number,
	almoxarifes: number,
	funcionarios: number,
	manual: number,
): string {
	const parts = [
		`${engineers} eng`,
		`${almoxarifes} almox`,
		`${funcionarios} func`,
	];
	if (manual > 0) parts.push(`${manual} sem app`);
	return parts.join(" · ");
}

export function TeamKpis({ range }: { range?: WeekRange }) {
	const { data: workers, isLoading: loadingWorkers } = useWorkers();
	const { data: invites, isLoading: loadingInvites } = useInvites();
	const { has } = usePermissions();
	const canListTeam = has("time_entries:list_team");
	const todayEntries = useTeamTimeEntries(
		dayRange(currentWorkDay()),
		canListTeam,
	);
	const periodDays = useAttendanceDays(
		range ?? weekRange(currentWorkDay()),
		canListTeam,
	);

	if (loadingWorkers || loadingInvites) {
		return (
			<div className="team-kpis">
				{[0, 1, 2, 3].map((i) => (
					<Skeleton key={i} className="team-kpis__skeleton" />
				))}
			</div>
		);
	}

	const list = activeWorkers(workers);
	const count = (role: string) =>
		list.filter((worker) => worker.role === role).length;
	const engineers = count("engineer");
	const almoxarifes = count("almoxarife");
	const funcionarios = count("funcionario");
	const clients = count("cliente");
	const manual = count("");
	const crew = engineers + almoxarifes + funcionarios + manual;

	const flagged = list.filter((worker) => worker.timeClockEnabled).length;
	const present = countPresentWorkers(todayEntries.data ?? []);
	const pending = countPendingDays(periodDays.data ?? []);

	return (
		<div className="team-kpis" data-columns={canListTeam ? "6" : "4"}>
			<KpiCard
				icon={Users}
				accent="primary"
				value={String(list.length)}
				label="Total de pessoas"
				sub="Ativas neste canteiro"
			/>
			{canListTeam && (
				<KpiCard
					icon={CheckCircle2}
					accent="success"
					value={`${present}/${flagged}`}
					label="Presentes agora"
					sub="Com o expediente aberto"
				/>
			)}
			{canListTeam && (
				<KpiCard
					icon={CircleAlert}
					accent={pending > 0 ? "warning" : "muted"}
					value={String(pending)}
					label="Pendências"
					sub="Dias sem ponto no período"
				/>
			)}
			<KpiCard
				icon={HardHat}
				accent="success"
				value={String(crew)}
				label="Equipe operacional"
				sub={crewSummary(engineers, almoxarifes, funcionarios, manual)}
			/>
			<KpiCard
				icon={User}
				accent="violet"
				value={String(clients)}
				label="Clientes"
				sub="Acompanham o canteiro"
			/>
			<KpiCard
				icon={MailPlus}
				accent="warning"
				value={String(invites?.length ?? 0)}
				label="Convites pendentes"
				sub="Aguardando aceite"
			/>
		</div>
	);
}
